import React, { FC, useMemo, useState } from "react";
import { SkillValidationErrors } from "@builder/skill/utils/validation";
import {
  SkillBuilder_SkillVersionFragment,
  SkillBuilder_VerificationStepFragment,
} from "../SkillDetailPage.generated";

export type SetSkillValidationErrorsFn = (
  fn: (oldValidationErrors: SkillValidationErrors) => SkillValidationErrors,
) => void;

type Props = {
  skillVersion: SkillBuilder_SkillVersionFragment | null | undefined;
  setSkillVersion?: (
    courseVersion: SkillBuilder_SkillVersionFragment | undefined | null,
  ) => void;
  validationErrors?: SkillValidationErrors;
  setValidationErrors?: SetSkillValidationErrorsFn;
  children?: JSX.Element;
};

export interface SkillBuilderContextState {
  skillId: number | undefined;
  skillVersion: SkillBuilder_SkillVersionFragment | null | undefined;
  validationErrors?: SkillValidationErrors;
  setValidationErrors: SetSkillValidationErrorsFn | undefined;
  activeStepId: string | null;
  activeStepPosition: number | null;
  activeStep?: SkillBuilder_VerificationStepFragment;
  setActiveStepId: (stepId: string | null) => void;
  draftVersionSelected: boolean;
  publishedVersionSelected: boolean;
}

export const SkillBuilderContext = React.createContext<
  SkillBuilderContextState | undefined
>(undefined);

const SkillBuilderStateProvider: FC<Props> = (props) => {
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const activeStepPosition = useMemo(() => {
    const index = props.skillVersion?.verificationSteps.findIndex(
      (x) => x.id === activeStepId,
    );
    return index === -1 ? null : index === undefined ? null : index;
  }, [props.skillVersion?.verificationSteps, activeStepId]);
  const activeStep = useMemo(() => {
    return props.skillVersion?.verificationSteps.find(
      (x) => x.id === activeStepId,
    );
  }, [props.skillVersion?.verificationSteps, activeStepId]);
  const contextState: SkillBuilderContextState = useMemo(() => {
    return {
      skillId: props.skillVersion?.skill.id,
      skillVersion: props.skillVersion,
      draftVersionSelected:
        props.skillVersion?.id === props.skillVersion?.skill.draftVersion?.id,
      publishedVersionSelected:
        props.skillVersion?.id ===
        props.skillVersion?.skill.publishedVersion?.id,
      activeStep,
      activeStepId,
      activeStepPosition,
      setActiveStepId,
      validationErrors: props.validationErrors,
      setValidationErrors: props.setValidationErrors,
    };
  }, [
    props.skillVersion,
    props.validationErrors,
    props.setValidationErrors,
    activeStep,
    activeStepId,
    activeStepPosition,
  ]);

  return (
    <SkillBuilderContext.Provider value={contextState}>
      {props.children}
    </SkillBuilderContext.Provider>
  );
};
export default SkillBuilderStateProvider;
